import { ErrorCategory } from '../constants';
import { IdpErrorName } from '../shared/idp-error-names';

type AuthErrorCategory = ErrorCategory;

const AMPLIFY_INVALID_CREDENTIALS = new Set<string>([
  IdpErrorName.NotAuthorized,
  IdpErrorName.UserNotFound,
]);

const AMPLIFY_VALIDATION = new Set<string>([
  IdpErrorName.UserNotConfirmed,
  IdpErrorName.UsernameExists,
  IdpErrorName.InvalidParameter,
  IdpErrorName.InvalidPassword,
  IdpErrorName.PasswordResetRequired,
]);

const AMPLIFY_API = new Set<string>([
  IdpErrorName.LimitExceeded,
  IdpErrorName.TooManyRequests,
]);

const REGISTER_VALIDATION_ATTRIBUTES = new Set([
  'email', 'password', 'first_name', 'last_name', 'home_phone',
  'firstName', 'lastName', 'homePhone',
]);

/**
 * Maps a thrown error to a coarse `ErrorCategory` for observability bucketing.
 *
 * Resolution order:
 * 1. Amplify error name (most specific — direct Cognito SDK errors)
 * 2. JSON:API `errors` array with a `source.pointer` or `attribute` field
 * 3. Falls back to `UnknownError`
 *
 * String-matching on error messages is intentionally avoided — it is fragile
 * and produces false positives as Cognito error messages change.
 */
export function detectErrorCategory(
  error: unknown,
  context: 'signin' | 'register',
): AuthErrorCategory {
  if (!error) return ErrorCategory.UnknownError;

  if (typeof error === 'object' && error !== null && 'name' in error) {
    const name = (error as { name: unknown }).name;
    if (typeof name === 'string' && name.length > 0) {
      if (AMPLIFY_INVALID_CREDENTIALS.has(name)) return ErrorCategory.InvalidCredentials;
      if (AMPLIFY_VALIDATION.has(name)) return ErrorCategory.ValidationRejected;
      if (AMPLIFY_API.has(name)) return ErrorCategory.ApiCallFailed;
      return ErrorCategory.UnknownError;
    }
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'errors' in error &&
    Array.isArray((error as { errors: unknown }).errors)
  ) {
    const err = error as { errors?: Array<{ source?: { pointer?: string }; attribute?: string }> };

    if (context === 'signin') return ErrorCategory.InvalidCredentials;

    if (context === 'register') {
      const hasValidationError = err.errors?.some(e => {
        const pointer = e.source?.pointer?.toLowerCase() ?? '';
        const attribute = (e.attribute ?? '').toLowerCase();
        return (
          REGISTER_VALIDATION_ATTRIBUTES.has(attribute) ||
          [...REGISTER_VALIDATION_ATTRIBUTES].some(a => pointer.includes(a))
        );
      });
      return hasValidationError ? ErrorCategory.ValidationRejected : ErrorCategory.ApiCallFailed;
    }
  }

  return ErrorCategory.UnknownError;
}
